import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

const SegmentedControl = ({ name, options, value, onChange }) => {
  const containerRef = useRef()
  const mirrorRef = useRef()
  const activeRef = useRef()
  const isFirstMount = useRef(true)
  const [ indicator, setIndicator ] = useState(null)
  const [ animating, setAnimating ] = useState(false)
  const [ vertical, setVertical ] = useState(false)

  const measure = () => {
    const el = activeRef.current
    if (!el) return
    setIndicator({
      left: el.offsetLeft,
      top: el.offsetTop,
      width: el.offsetWidth,
      height: el.offsetHeight,
    })
  }

  // Hidden mirror always lays out horizontally with no flex-grow and no
  // wrap, so its scrollWidth equals "the smallest container width that
  // would fit everything horizontally". If that's wider than what the
  // visible container actually has, switch to vertical stacking. The
  // check is symmetric — whether we're currently horizontal or vertical
  // doesn't matter, because the mirror is a stable, layout-independent
  // measurement of horizontal cost.
  const checkLayout = () => {
    if (!containerRef.current || !mirrorRef.current) return
    setVertical(mirrorRef.current.scrollWidth > containerRef.current.clientWidth)
  }

  useLayoutEffect(() => {
    checkLayout()
    measure()
    if (isFirstMount.current) isFirstMount.current = false
    else setAnimating(true)
  }, [ value, options, vertical ])

  useEffect(() => {
    if (!containerRef.current) return
    const observer = new ResizeObserver(() => {
      setAnimating(false)
      checkLayout()
      measure()
    })
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  return (
    <div className="relative">
      <div
        ref={containerRef}
        role="radiogroup"
        className={[
          'group relative flex gap-1 rounded-xl bg-brand-100 p-1',
          vertical ? 'flex-col' : 'flex-wrap',
        ].join(' ')}
      >
        {indicator && (
          <span
            aria-hidden="true"
            className={[
              'absolute rounded-lg bg-white shadow',
              'group-has-focus-visible:outline-2 group-has-focus-visible:outline-solid group-has-focus-visible:outline-(--btn-radio-focus-ring)',
              animating && 'transition-all duration-200',
            ].filter(Boolean).join(' ')}
            style={indicator}
          />
        )}
        {options.map(option => {
          const selected = option.value === value
          return (
            <label
              key={option.value}
              ref={selected ? activeRef : null}
              className={[
                'relative z-10 cursor-pointer rounded px-1 py-2 text-center text-sm font-medium transition-colors @roomy:px-3 @roomy:text-base',
                vertical ? 'w-full' : 'flex-1 text-nowrap',
                selected ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700',
              ].join(' ')}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={selected}
                onChange={onChange}
                className="sr-only"
              />
              {option.label}
            </label>
          )
        })}
      </div>

      {/* Hidden horizontal mirror for layout cost measurement. Mirrors the
          visible labels' width-contributing styles (padding, font, gap)
          plus text-nowrap so each "label" takes its intrinsic content
          width. w-max keeps the wrapper sized to its children rather than
          to the parent. invisible + pointer-events-none + absolute keeps
          it out of paint, hit-testing, and document flow; aria-hidden
          keeps it out of the accessibility tree. Lives outside the
          radiogroup so the role's children stay strictly radios. */}
      <div
        ref={mirrorRef}
        aria-hidden="true"
        className="invisible pointer-events-none absolute inset-s-0 top-0 flex w-max gap-1 p-1"
      >
        {options.map(option => (
          <span
            key={option.value}
            className="px-1 py-2 text-sm font-medium text-nowrap @roomy:px-3 @roomy:text-base"
          >
            {option.label}
          </span>
        ))}
      </div>
    </div>
  )
}

export default SegmentedControl
